import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePayables, Payable } from '../../payables/useData';
import { useTheme } from '../../../contexts/ThemeContext';
import GenericListWidget from './GenericListWidget';

export interface RecentPayablesProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentPayables: React.FC<RecentPayablesProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: payables = [], isLoading } = usePayables(undefined, 'pending');
  const arr = Array.isArray(payables) ? payables : [];

  const items = React.useMemo(() => {
    return [...arr]
      .sort((a: Payable, b: Payable) => {
        const da = new Date(a.dueDate || a.invoiceDate || 0).getTime();
        const db = new Date(b.dueDate || b.invoiceDate || 0).getTime();
        return da - db;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const formatCurrency = (v?: number) =>
    v != null ? `€${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '';

  return (
    <GenericListWidget<Payable>
      title="Recent Payables"
      viewAllPath="/payables?status=pending"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No pending payables"
      getItemKey={(p) => p.id}
      embedded={embedded}
      renderItem={(payable) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(`/payables/${payable.id}`)}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(`/payables/${payable.id}`)
          }
          className="flex items-start gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm font-medium ${colors.text}`}>
              {payable.payeeName || `Payable #${payable.id}`}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {formatCurrency(payable.amount)}
              {payable.dueDate &&
                ` · Due ${new Date(payable.dueDate).toLocaleDateString('en-US')}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentPayables;
